/** @jsx jsx */
import React, { type ReactNode, useMemo, useCallback } from 'react';
import { styled, jsx, cssMap } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { SHORTCUT_ELEMENT_TYPES } from '../../../../../constants.tsx';
import type { ShortcutSpecialChar } from '../../../../../types/index.tsx';
import { getShortcutSpecialCharacters } from '../../../../../utils.tsx';
import { FilledLozenge } from '../../../../custom-lozenge/index.tsx';
import {
	SHORTCUT_DIVIDER_TEST_ID,
	SHORTCUT_ICON_TEST_ID,
	SHORTCUT_WRAPPER_TEST_ID,
} from './constants.tsx';
import { messages } from './messages.tsx';
import type { AccumulatorType, ShortcutsIconsProps } from './types.tsx';

interface KeyElementProps {
	shortcut?: string;
}

const KeyElementNew = ({ shortcut }: KeyElementProps) => {
	const isLongText = shortcut && shortcut.length > 1;
	return (
		<div css={styles.shortcutKeyWrapper}>
			<span
				css={[styles.lozenge, isLongText && styles.longLozenge]}
				data-testid={SHORTCUT_ICON_TEST_ID}
			>
				{shortcut}
			</span>
		</div>
	);
};

const KeyElementOld = ({ shortcut }: KeyElementProps) => (
	<ShortcutKeyWrapper>
		<FilledLozenge data-testid={SHORTCUT_ICON_TEST_ID}>{shortcut}</FilledLozenge>
	</ShortcutKeyWrapper>
);

const KeyElement = componentWithFG<KeyElementProps, KeyElementProps>(
	'command-palette-display-shift-modifier-shortcut',
	KeyElementNew,
	KeyElementOld,
);

const DivisorElement = ({ divisor }: { divisor: ReactNode[] | ReactNode }) => (
	<ShortcutDivider data-testid={SHORTCUT_DIVIDER_TEST_ID}>{divisor}</ShortcutDivider>
);

const SHIFT_CHAR_MAP: Record<string, string> = {
	'?': 'shift /',
	'!': 'shift 1',
	'@': 'shift 2',
	'#': 'shift 3',
	$: 'shift 4',
	'%': 'shift 5',
	'^': 'shift 6',
	'&': 'shift 7',
	'*': 'shift 8',
	'(': 'shift 9',
	')': 'shift 0',
	_: 'shift -',
	'+': 'shift =',
	'{': 'shift [',
	'}': 'shift ]',
	':': 'shift ;',
	'"': "shift '",
	'<': 'shift ,',
	'>': 'shift .',
	'|': 'shift \\',
	'~': 'shift `',
};

const shiftModifierCheck = (shortcut: string): string => {
	if (shortcut.length === 1) {
		if (/[A-Z]/.test(shortcut)) {
			return `shift ${shortcut.toLowerCase()}`;
		}
		return SHIFT_CHAR_MAP[shortcut] || shortcut;
	}
	return shortcut;
};

const parseShortcut = (
	shortcut: string,
	replacers: ShortcutSpecialChar[],
	acc: AccumulatorType[] = [],
	types: SHORTCUT_ELEMENT_TYPES[] = [],
): [AccumulatorType[], SHORTCUT_ELEMENT_TYPES[]] => {
	const replacer = replacers.shift();

	if (!replacer) {
		fg('command-palette-display-shift-modifier-shortcut')
			? shiftModifierCheck(shortcut)
					.split(' ')
					.forEach((key, index, array) => {
						acc.push({
							type: SHORTCUT_ELEMENT_TYPES.KEY,
							key,
						});
						types.push(SHORTCUT_ELEMENT_TYPES.KEY);
						if (index < array.length - 1) {
							const separatorType = SHORTCUT_ELEMENT_TYPES.PLUS;
							acc.push({ type: separatorType });
							types.push(separatorType);
						}
					})
			: (acc.push({
					type: SHORTCUT_ELEMENT_TYPES.KEY,
					key: shortcut,
				}),
				types.push(SHORTCUT_ELEMENT_TYPES.KEY));
		return [acc, types];
	}

	shortcut.split(replacer.char).forEach((str, index, self) => {
		parseShortcut(str, replacers.slice(0), acc, types);

		if (index < self.length - 1) {
			acc.push({ type: replacer.replacement });
			types.push(replacer.replacement);
		}
	});

	return [acc, types];
};

export const useShortcutIntl = () => useIntl();

export const Shortcuts = ({ shortcut }: ShortcutsIconsProps) => {
	const { formatMessage } = useShortcutIntl();

	const [shortcuts, types] = useMemo(
		() => parseShortcut(shortcut, getShortcutSpecialCharacters()),
		[shortcut],
	);

	const basicShortcutMessage = useMemo(() => {
		if (types.length === 3) {
			if (types[0] === SHORTCUT_ELEMENT_TYPES.KEY && types[2] === SHORTCUT_ELEMENT_TYPES.KEY) {
				if (types[1] === SHORTCUT_ELEMENT_TYPES.THEN) {
					return messages.shortcutThen;
				}
				if (types[1] === SHORTCUT_ELEMENT_TYPES.OR) {
					return messages.shortcutOr;
				}
				if (types[1] === SHORTCUT_ELEMENT_TYPES.PLUS) {
					return messages.shortcutPlus;
				}
			}
		}
		return null;
	}, [types]);

	const formatTypeToMessage = useCallback(
		(type: SHORTCUT_ELEMENT_TYPES) => {
			switch (type) {
				case SHORTCUT_ELEMENT_TYPES.THEN:
					return formatMessage(messages.divisorThen);
				case SHORTCUT_ELEMENT_TYPES.OR:
					return formatMessage(messages.divisorOr);
				default:
					return formatMessage(messages.divisorPlus);
			}
		},
		[formatMessage],
	);

	return (
		<ShortcutContainer data-testid={SHORTCUT_WRAPPER_TEST_ID} aria-hidden>
			{basicShortcutMessage
				? formatMessage(basicShortcutMessage, {
						shortcutA: <KeyElement shortcut={shortcuts[0].key} />,
						shortcutB: <KeyElement shortcut={shortcuts[2].key} />,
						divisor: (chunk) => <DivisorElement divisor={chunk} />,
					})
				: shortcuts.map((obj, index) => {
						if (obj.type === SHORTCUT_ELEMENT_TYPES.KEY) {
							return <KeyElement key={index} shortcut={obj.key} />;
						}

						return <DivisorElement key={index} divisor={formatTypeToMessage(obj.type)} />;
					})}
		</ShortcutContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ShortcutDivider = styled.div({
	marginTop: 0,
	marginRight: token('space.050'),
	marginBottom: 0,
	marginLeft: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ShortcutContainer = styled.div({
	display: 'flex',
	flexGrow: 0,
	flexShrink: 1,
	justifyContent: 'center',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ShortcutKeyWrapper = styled.div({
	width: '24px',
	height: '24px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

const styles = cssMap({
	lozenge: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: token('border.radius'),
		flexShrink: 0,
		height: '24px',
		font: token('font.code'),
		color: token('color.text'),
		minWidth: '24px',
		width: 'max-content',
		position: 'relative',
		textTransform: 'capitalize',
		zIndex: 1,
		'&::before': {
			position: 'absolute',
			content: '',
			background: `linear-gradient(230.8deg, ${token('color.background.accent.blue.subtler.pressed')} 29.94%, ${token('color.background.accent.purple.subtler')} 119.78%)`,
			opacity: 0.2,
			borderRadius: token('border.radius'),
			top: 0,
			bottom: 0,
			right: 0,
			left: 0,
			zIndex: -1,
		},
	},
	longLozenge: {
		paddingInline: token('space.075'),
	},
	shortcutKeyWrapper: {
		minWidth: '24px',
		height: '24px',
		padding: '0 4px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
});
